import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Carrusel de banners del inicio.
 *
 * UNA SOLA DIAPOSITIVA, SIN CARRUSEL. Si la campaña de temporada venció y
 * queda un banner, se dibuja tal cual, sin flechas ni puntos. Unos controles
 * para moverse entre una sola cosa son ruido, y la alternativa —dejar el
 * carrusel con un solo slide— deja al visitante esperando algo que no llega.
 *
 * LAS DOS DIAPOSITIVAS ESTÁN SIEMPRE EN EL DOM. No se monta y desmonta la que
 * se ve: las dos se apilan en la misma celda de una rejilla y la inactiva se
 * oculta. Importa por tres razones — el buscador y los crawlers de IA leen las
 * dos, el alto del contenedor es el de la más alta y por lo tanto no hay salto
 * de layout al cambiar, y la foto de la segunda alcanza a descargarse antes de
 * que alguien la pida.
 *
 * `invisible` Y NO `opacity-0` A SECAS. Con solo opacidad, los enlaces de la
 * diapositiva oculta siguen recibiendo foco con el tabulador y el lector de
 * pantalla los sigue anunciando: se navega a ciegas por un banner que no está
 * en pantalla. `visibility: hidden` la saca del orden de tabulación y del
 * árbol de accesibilidad, y aun así conserva su espacio, que es lo que
 * sostiene el alto del contenedor.
 *
 * EL AVANCE AUTOMÁTICO SE DETIENE SOLO cuando alguien pasa el mouse, mueve el
 * foco con el teclado o se va a otra pestaña. Un banner que cambia mientras
 * se está leyendo es la queja clásica del carrusel, y pararlo al primer gesto
 * de atención la elimina sin quitarle la rotación a quien solo mira.
 *
 * Y NO ROTA si el sistema pide menos movimiento (`prefers-reduced-motion`):
 * ahí el carrusel queda en la primera y se cambia solo a mano.
 */

const PAUSA_MS = 7000;

const FotoCarrusel = ({ children, etiquetas = [], label = "Destacados" }) => {
  const slides = React.Children.toArray(children).filter(Boolean);
  const [activa, setActiva] = useState(0);
  const [detenido, setDetenido] = useState(false);
  const contenedor = useRef(null);
  const toque = useRef(null);

  const total = slides.length;
  const ir = useCallback((i) => setActiva(((i % total) + total) % total), [total]);

  /* Rotación. Se arma de nuevo en cada cambio de `activa` para que, al tocar
     una flecha, el próximo salto cuente desde ahí y no desde el tiempo que
     llevaba corriendo el anterior. */
  useEffect(() => {
    if (total < 2 || detenido) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    const t = setTimeout(() => ir(activa + 1), PAUSA_MS);
    return () => clearTimeout(t);
  }, [activa, detenido, total, ir]);

  /* En una pestaña de fondo no hay nadie mirando: rotar ahí solo gasta. */
  useEffect(() => {
    const alCambiar = () => setDetenido(document.hidden);
    document.addEventListener("visibilitychange", alCambiar);
    return () => document.removeEventListener("visibilitychange", alCambiar);
  }, []);

  const alTeclado = (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      ir(activa - 1);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      ir(activa + 1);
    }
  };

  /*
    Deslizar con el dedo. En el telefono los controles quedan bajo el pliegue
    —la diapositiva es mas alta que la pantalla—, asi que sin esto la unica
    forma de ver el segundo banner seria esperar a que rote.

    Se exige que el movimiento horizontal gane al vertical para no robarle el
    scroll a la pagina: un arrastre casi vertical tiene que seguir bajando.
  */
  const alTocar = (event) => {
    const t0 = event.touches[0];
    toque.current = { x: t0.clientX, y: t0.clientY };
  };

  const alSoltar = (event) => {
    if (!toque.current) return;
    const t1 = event.changedTouches[0];
    const dx = t1.clientX - toque.current.x;
    const dy = t1.clientY - toque.current.y;
    toque.current = null;
    if (Math.abs(dx) < 45 || Math.abs(dx) <= Math.abs(dy)) return;
    ir(activa + (dx < 0 ? 1 : -1));
  };

  if (total === 0) return null;
  if (total === 1) return slides[0];

  return (
    <div
      ref={contenedor}
      className="relative bg-navy-950"
      role="region"
      aria-roledescription="carrusel"
      aria-label={label}
      onMouseEnter={() => setDetenido(true)}
      onMouseLeave={() => setDetenido(false)}
      onFocusCapture={() => setDetenido(true)}
      onBlurCapture={() => setDetenido(false)}
      onKeyDown={alTeclado}
      onTouchStart={alTocar}
      onTouchEnd={alSoltar}
    >
      <div className="grid">
        {slides.map((slide, i) => (
          <div
            key={etiquetas[i] || i}
            className={`col-start-1 row-start-1 transition-[opacity,visibility] duration-700 motion-reduce:transition-none ${
              i === activa ? "visible opacity-100" : "invisible opacity-0"
            }`}
            role="group"
            aria-roledescription="diapositiva"
            aria-label={`${i + 1} de ${total}${etiquetas[i] ? `: ${etiquetas[i]}` : ""}`}
          >
            {slide}
          </div>
        ))}
      </div>

      {/* Los controles van centrados abajo y no a la derecha: ahí vive el
          botón flotante de WhatsApp y se pisarían. */}
      <div className="absolute inset-x-0 bottom-4 z-20 flex items-center justify-center gap-2 lg:bottom-6">
        <button
          type="button"
          onClick={() => ir(activa - 1)}
          aria-label="Banner anterior"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-white/25 bg-navy-950/60 text-white backdrop-blur-sm transition hover:bg-navy-950/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sand-300"
        >
          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
        </button>

        <ul className="flex items-center gap-2 px-1">
          {slides.map((slide, i) => (
            <li key={etiquetas[i] || i}>
              <button
                type="button"
                onClick={() => ir(i)}
                aria-label={etiquetas[i] ? `Ver ${etiquetas[i]}` : `Ver banner ${i + 1}`}
                aria-current={i === activa ? "true" : undefined}
                className={`block h-2.5 rounded-full transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sand-300 ${
                  i === activa ? "w-7 bg-sand-400" : "w-2.5 bg-white/40 hover:bg-white/70"
                }`}
              />
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={() => ir(activa + 1)}
          aria-label="Banner siguiente"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-white/25 bg-navy-950/60 text-white backdrop-blur-sm transition hover:bg-navy-950/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sand-300"
        >
          <ChevronRight className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default FotoCarrusel;
